import { memo } from "react";
import { useOptionalTooltipDispatchRef } from "../../hooks/TooltipStateIsolationProvider";
import type { TooltipProductDatum } from "../../types/atlasTooltip";
import type { ProcessedNode } from "./types";
import type { HierarchyNode } from "d3-hierarchy";
import type { ProductMetadatum } from "@atlas-int/data/types";

interface ProductSpacePlotProps {
  processedData: ProcessedNode[];
  metaDataLookup: Map<string, HierarchyNode<ProductMetadatum>>;
  highlightedItem: string | undefined;
  select: (id: string | null) => void;
  setRingItem: (id: string | null) => void;
  setHighlightedItem: (id: string | undefined) => void;
  onAnalyticsEvent?: (category: string, action: string, label: string, context: string) => void;
}

const ProductSpacePlot = memo(
  ({
    processedData,
    metaDataLookup,
    highlightedItem,
    select,
    setRingItem,
    setHighlightedItem,
    onAnalyticsEvent,
  }: ProductSpacePlotProps) => {
    const dispatchRef = useOptionalTooltipDispatchRef();

    return (
      <>
        {processedData.map((node, i) => {
          const product = metaDataLookup.get(node.productId)?.data;
          return (
            <circle
              key={`node-${node.productId}-${i}`}
              cx={node.x}
              cy={node.y}
              r={node.r}
              fill={node.fill}
              strokeWidth="1"
              stroke="grey"
              style={{ cursor: "pointer" }}
              onMouseEnter={() => {
                select(node.productId);
                if (dispatchRef && highlightedItem !== node.productId) {
                  dispatchRef.current.showTooltip({
                    kind: "product",
                    nameEn: product?.nameShortEn,
                    productId: node.productId,
                  } as TooltipProductDatum);
                }
              }}
              onMouseLeave={() => {
                select(null);
                dispatchRef?.current.hideTooltip();
              }}
              onClick={() => {
                setHighlightedItem(undefined);
                setRingItem(node.productId);
                onAnalyticsEvent?.(
                  "node",
                  "click",
                  "productspace",
                  "explore",
                );
              }}
            />
          );
        })}
      </>
    );
  },
);

ProductSpacePlot.displayName = "ProductSpacePlot";
export default ProductSpacePlot;
